import { useState, useEffect } from "react";

export enum SCREEN_SIZE {
  XS = "xs",
  SM = "sm",
  MD = "md",
  LG = "lg",
  XL = "xl",
}

export enum BREAKPOINTS {
  xs = 480,
  sm = 650,
  md = 900,
  lg = 1200,
  xl = 1440,
}

export const useResponsiveness = (shouldPreventServerCalculation = false) => {
  const windowVal = typeof window !== "undefined" ? window.innerWidth || null : null;
  const [windowWidth, setWindowWidth] = useState<number | null>(
    shouldPreventServerCalculation ? windowVal : null,
  );
  const [orientation, setOrientation] = useState<"portrait" | "landscape" | null>(null);

  useEffect(() => {
    const updateWindowWidth = () => setWindowWidth(window.innerWidth);

    const mql = window.matchMedia("(orientation: portrait)");

    const updateOrientation = () => setOrientation(mql.matches ? "portrait" : "landscape");

    // Initial values
    updateWindowWidth();
    updateOrientation();

    // Listeners
    window.addEventListener("resize", updateWindowWidth);
    mql.addEventListener("change", updateOrientation);

    return () => {
      window.removeEventListener("resize", updateWindowWidth);
      mql.removeEventListener("change", updateOrientation);
    };
  }, []);

  // ⚠ During SSR or before effect runs, values are null
  const safeOrientation = orientation ?? "landscape";

  const isAbove = (bp: keyof typeof BREAKPOINTS, inclusive = true, preferred = false) => {
    if (windowWidth) {
      return inclusive ? windowWidth >= BREAKPOINTS[bp] : windowWidth > BREAKPOINTS[bp];
    }
    return preferred;
  };
  const isBelow = (bp: keyof typeof BREAKPOINTS, inclusive = true) =>
    windowWidth !== null &&
    (inclusive ? windowWidth < BREAKPOINTS[bp] : windowWidth <= BREAKPOINTS[bp]);
  const isBetween = (
    min: keyof typeof BREAKPOINTS,
    max: keyof typeof BREAKPOINTS,
    inclusive = true,
  ) =>
    windowWidth !== null &&
    (inclusive
      ? windowWidth >= BREAKPOINTS[min] && windowWidth <= BREAKPOINTS[max]
      : windowWidth > BREAKPOINTS[min] && windowWidth < BREAKPOINTS[max]);

  return {
    windowWidth,
    isAbove,
    isBelow,
    isBetween,
    isPortrait: safeOrientation === "portrait",
    isLandscape: safeOrientation === "landscape",
  };
};
